import { Component, TemplateRef, ViewChild } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { UserService } from "@app/main/modules/foundation/_services/user.service";
import { TranslationService } from "@app/services/translation.service";
import { take } from "rxjs/operators";
import { NpsGaugeHorizontal } from "../nps-gauge-horizontal/nps-gauge-horizontal.component";
import { ICalculatedNPSScore, INPSAggregatedScore, INPSCalculatedScore, INPSEntry, NPSService } from "../nps.service";
import "./nps-dashboard.component.scss";

export interface IDonutSlice {
    name: string;
    count: number;
    percentage: number;
    remainder: number;
    offset: number;
    color: string;
}

@Component({
    selector: "nps-dashboard",
    template: require("./nps-dashboard.component.html")
})
export class NPSDashboardComponent {
    @ViewChild('scoreTemplate') scoreTemplate: TemplateRef<any>;

    ratings: INPSEntry[];
    score: ICalculatedNPSScore;

    scoreHistogram: INPSCalculatedScore[] = [];

    resellerScores: INPSAggregatedScore[] = [];
    customerScores: INPSAggregatedScore[] = [];
    happyCustomers: INPSAggregatedScore[] = [];
    unhappyCustomers: INPSAggregatedScore[] = [];

    donutSlices: IDonutSlice[] = [];

    selectedRating: INPSEntry;
    selectedUserRecentRatings: INPSEntry[];
    selectedUserEmail: string;

    resultsTableColumns: IVTableColumn<INPSEntry>[];
    selectedRatingTableColumns: IVTableColumn<INPSEntry>[];
    resultsTableRows: IVTableRow[];

    summaryTableColumns1: IVTableColumn<INPSAggregatedScore>[];
    summaryTableColumns2: IVTableColumn<INPSAggregatedScore>[];
    summaryTableColumns3: IVTableColumn<INPSAggregatedScore>[];
    resellersSummaryTableRows: IVTableRow[];
    happiestSummaryTableRows: IVTableRow[];
    unhappySummaryTableRows: IVTableRow[];
    selectedRatingTableRows: IVTableRow[];

    hideInternal: boolean = true;
    hideCustomers: boolean;

    loading = false;

    constructor(
        private readonly npsService: NPSService,
        private readonly translationService: TranslationService,
        private readonly userService: UserService,
        private readonly vTableService: VTableService) {
    }

    ngOnInit(): void {
        this.loadData();
        this.createTables();
    }

    onRatingClick(event: IVTableRow) {
        this.selectRating(event.data);
    }

    selectRating(rating: INPSEntry) {

        this.selectedRating = rating;

        this.selectedUserRecentRatings = this.ratings.filter(r => r.UserId == rating.UserId);

        this.getSelectedUserEmail();

        this.selectedRatingTableRows = this.vTableService.createTableRows(this.ratings.filter(r => r.UserId == rating.UserId), this.selectedRatingTableColumns);
    }

    closeSelectedRating(): void {
        this.selectedRating = null;
        this.selectedUserEmail = null;
        this.selectedRatingTableRows = [];
    }

    onTabSelected(tab: VTabComponent): void {

        this.hideCustomers = tab.tabIndex === 1;
        this.hideInternal = tab.tabIndex === 0;

        this.loadData();
    }

    // ---

    private loadData() {
        this.loading = true;

        this.npsService.getRatings({ populateCustomers: true, hideInternal: this.hideInternal, hideCustomers: this.hideCustomers }).then(ratings => {
            this.ratings = ratings;

            this.npsService.prepareNPSEntriesForDisplay(ratings);

            this.score = this.npsService.calculateNPSScore(ratings);
            this.scoreHistogram = this.npsService.calculateScoreHistogram(ratings);
            this.customerScores = this.npsService.calculateAggregatedScoresByCustomer(ratings);
            this.resellerScores = this.npsService.calculateAggregatedScoresByReseller(ratings);

            const numCustomers = 2;
            this.customerScores.sort((a, b) => b.score.score - a.score.score);
            this.happyCustomers = this.customerScores.slice(0, numCustomers);
            this.unhappyCustomers = this.customerScores.slice(-numCustomers);

            this.calculateDonut();
            this.createTableRows();

            this.loading = false;
        });
    }

    private calculateDonut() {
        this.donutSlices = [];
        this.createDonutSlice(this.translationService.t("NpsRater.Detractors"), this.score.detractor.count, this.score.detractor.percentage, this.npsService.getColor(0));
        this.createDonutSlice(this.translationService.t("NpsRater.Passives"), this.score.passive.count, this.score.passive.percentage, this.npsService.getColor(7));
        this.createDonutSlice(this.translationService.t("NpsRater.Promoters"), this.score.promoter.count, this.score.promoter.percentage, this.npsService.getColor(10));
    }

    private createDonutSlice(label: string, count: number, percentage: number, color: string) {

        const initialOffsetPercentage = 50; // was 25
        const offset = 100 - this.donutSlices.reduce((a, b) => a + b.percentage, 0) + initialOffsetPercentage;

        // This allows us to make a half circle, 1 is full circle, 0.5 is half
        let percentModifier = 0.5;

        this.donutSlices.push({
            name: label,
            count: count,
            percentage: percentage * percentModifier,
            remainder: 100 - percentage * percentModifier,
            offset: offset,
            color: color
        });
    }

    private getSelectedUserEmail(): void {
        if (!this.selectedRating) {
            return;
        }

        this.userService.getUsers({ Id: +this.selectedRating.UserId }).pipe(take(1)).subscribe(users => {
            this.selectedUserEmail = users[0]?.Email;
        });

    }

    private createTables(): void {
        this.resultsTableColumns = this.createResultsTable(true);
        this.selectedRatingTableColumns = this.createResultsTable(false);
    }

    private createTableRows(): void {
        this.resultsTableRows = this.vTableService.createTableRows(this.ratings, this.resultsTableColumns);

        // Need separate column arrays even though the tables are identical, otherwise sorting a column will sort all 3 tables at once.
        this.summaryTableColumns1 = this.createSummaryTable();
        this.summaryTableColumns2 = this.createSummaryTable();
        this.summaryTableColumns3 = this.createSummaryTable();

        this.resellersSummaryTableRows = this.vTableService.createTableRows(this.resellerScores, this.summaryTableColumns1);
        this.happiestSummaryTableRows = this.vTableService.createTableRows(this.happyCustomers, this.summaryTableColumns2);
        this.unhappySummaryTableRows = this.vTableService.createTableRows(this.unhappyCustomers,
            this.summaryTableColumns3);
    }

    private createResultsTable(includeCustomer: boolean): IVTableColumn<INPSEntry>[] {
        let columns = [
            {
                header: this.vTableService.createDateTableHeader({ sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: entry => this.vTableService.createDateCell(new Date(entry.DateTime))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Score', width: 110, sortType: SortType.Number, sortDirection: SortDirection.Descending }),
                createCellFunction: entry => {
                    const data = {
                        color: entry.color,
                        Score: entry.Score,
                        scoreLabel: entry.scoreLabel
                    }
                    return this.vTableService.createTableTemplateCell(data, this.scoreTemplate)
                }
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'Common.User' }),
                createCellFunction: entry => this.vTableService.createTableCell(entry.UserDescription)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Comments' }),
                createCellFunction: entry => this.vTableService.createTableCell(entry.Comments)
            }
        ]

        if (includeCustomer) {
            columns.splice(2, 0, {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Customer' }),
                createCellFunction: entry => this.vTableService.createTableCell(entry.customer.CompanyName)
            })


        }

        return columns;
    }

    private createSummaryTable(): IVTableColumn<INPSAggregatedScore>[] {
        return [
            {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Customer' }),
                createCellFunction: aggregatedScore => this.vTableService.createTableCell(aggregatedScore.description)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Score', width: 80, sortType: SortType.Number, sorted: true, sortDirection: SortDirection.Ascending }),
                createCellFunction: aggregatedScore => this.vTableService.createTableCell(aggregatedScore.score.score, { classes: 'text-right' })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: 'NpsRater.Responses', width: 100 }),
                createCellFunction: aggregatedScore => this.vTableService.createTableCell(aggregatedScore.ratings.length, { classes: 'text-right' })
            },
            {
                header: this.vTableService.createTableHeader({ title: "NPS", width: 100, sortable: false }),
                createCellFunction: aggregatedScore => this.vTableService.createTableCell(aggregatedScore.score.score, {
                    component: NpsGaugeHorizontal,
                    componentInputs: {
                        score: aggregatedScore.score
                    }
                })
            }
        ]
    }

}
